import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import "./AgencyAgentEmployeeTable.scss";
import TableHeaderRow from "../../../../components/tables/TableHeaderRow";
import TableHeaderCell from "../../../../components/tables/TableHeaderCell";
import TableDataRow from "../../../../components/tables/TableDataRow";
import TableDataCell from "../../../../components/tables/TableDataCell";
import { getCdnImageUrl, getImageUrl } from "../../../../utils/helpers/common";
import { AgencyEmployeeApi } from "../../../../utils/api/AgencyEmployeeApi";
import toast from "react-hot-toast";
import { AgentEmployeeApi } from "../../../../utils/api/AgentEmployeeApi";
import { CircularProgress, Modal } from "@mui/material";
import { IAgencyAgentEmployeeData } from "./AgencyAgentEmployeeList";

const DROPDOWN_WIDTH = 160;

const AgencyAgentEmployeeTable = ({
  listData,
  onRefresh,
}: {
  listData: IAgencyAgentEmployeeData[];
  onRefresh?: () => void;
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const [menuMeta, setMenuMeta] = useState<{
    anchorRect: DOMRect | null;
    anchorEl: HTMLElement | null;
  }>({ anchorRect: null, anchorEl: null });

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [statusOverride, setStatusOverride] = useState<Record<string, string>>(
    {},
  );

  type ConfirmType = "activate" | "deactivate" | "archive" | "unarchive";

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState<ConfirmType | null>(null);
  const [confirmEmp, setConfirmEmp] = useState<IAgencyAgentEmployeeData | null>(
    null,
  );
  const [confirmLoading, setConfirmLoading] = useState(false);

  const closeDropdown = () => {
    setOpenMenuId(null);
    setMenuMeta({ anchorRect: null, anchorEl: null });
  };

  const closeConfirm = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmType(null);
    setConfirmEmp(null);
  };

  const navigate = useNavigate();

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return moment(dateString).format("MMMM D, YYYY");
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return "-";
    return moment(dateString).format("hh:mm A");
  };

  const getStatusConfig = (status?: string) => {
    const normalized = (status || "").toLowerCase();

    if (normalized === "active")
      return { label: "Active", className: "active" };
    if (normalized === "deactivated")
      return { label: "Disabled", className: "deactivated" };
    if (normalized === "archived")
      return { label: "Archived", className: "archived" };

    const label = normalized
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

    return { label: label || "-", className: "deactivated" };
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (dropdownRef.current?.contains(target)) return;
      if (menuMeta.anchorEl?.contains(target)) return;

      setOpenMenuId(null);
      setMenuMeta({ anchorRect: null, anchorEl: null });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuMeta.anchorEl]);

  useEffect(() => {
    const close = () => {
      setOpenMenuId(null);
      setMenuMeta({ anchorRect: null, anchorEl: null });
    };

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, []);

  const getEmployeeProfileImageUrl = (uploadRecord: any) => {
    const fallbackImage = "/images/profile-placeholder-image.png";

    if (!uploadRecord) return fallbackImage;
    if (uploadRecord.medium_image_url)
      return getCdnImageUrl(uploadRecord.medium_image_url);
    if (uploadRecord.small_image_url)
      return getCdnImageUrl(uploadRecord.small_image_url);
    if (uploadRecord.storage_key)
      return getCdnImageUrl(uploadRecord.storage_key);
    if (uploadRecord.compressed_file_path)
      return getImageUrl(uploadRecord.compressed_file_path);
    if (uploadRecord.original_file_path)
      return getImageUrl(uploadRecord.original_file_path);
    return fallbackImage;
  };

  const getInitials = (name?: string, email?: string) => {
    const base = (name || "").trim();
    if (base) {
      const parts = base.split(" ").filter(Boolean);
      return `${(parts[0] || "U")[0]}${(parts[1] || "")[0] || ""}`.toUpperCase();
    }
    return (email || "U")[0].toUpperCase();
  };

  const updateStatus = async (employeeId: string, status: string) => {
    try {
      const res = await AgentEmployeeApi.update(employeeId, { status });

      if (res.success && res.data?.success) {
        toast.success("Status updated!");

        setStatusOverride((p) => ({ ...p, [employeeId]: status }));

        setOpenMenuId(null);
        setMenuMeta({ anchorRect: null, anchorEl: null });

        await onRefresh?.();

        return;
      }

      toast.error(res?.error || res?.data?.message || "Failed to update");
    } catch (e: any) {
      toast.error(e?.message || "Failed to update");
    }
  };

  const handleToggleActive = async (emp: IAgencyAgentEmployeeData) => {
    const current = (statusOverride[emp._id] ?? emp.status ?? "").toLowerCase();
    const nextStatus = current === "active" ? "deactivated" : "active";
    await updateStatus(emp._id, nextStatus);
  };

  const handleArchiveToggle = async (emp: IAgencyAgentEmployeeData) => {
    const current = (statusOverride[emp._id] ?? emp.status ?? "").toLowerCase();
    const nextStatus = current === "archived" ? "active" : "archived";
    await updateStatus(emp._id, nextStatus);
  };

  const openConfirmForActiveToggle = (emp: IAgencyAgentEmployeeData) => {
    const current = (statusOverride[emp._id] ?? emp.status ?? "").toLowerCase();
    const type: ConfirmType = current === "active" ? "deactivate" : "activate";

    setConfirmEmp(emp);
    setConfirmType(type);
    setConfirmOpen(true);
    closeDropdown();
  };

  const openConfirmForArchiveToggle = (emp: IAgencyAgentEmployeeData) => {
    const current = (statusOverride[emp._id] ?? emp.status ?? "").toLowerCase();
    const type: ConfirmType = current === "archived" ? "unarchive" : "archive";

    setConfirmEmp(emp);
    setConfirmType(type);
    setConfirmOpen(true);
    closeDropdown();
  };

  const dropdownStyle = (() => {
    const rect = menuMeta.anchorRect;
    if (!rect) return undefined;

    let left = rect.right - DROPDOWN_WIDTH;
    left = Math.max(8, Math.min(left, window.innerWidth - DROPDOWN_WIDTH - 8));

    return {
      position: "fixed" as const,
      top: rect.top,
      left,
      width: DROPDOWN_WIDTH,
      transform: "translateY(calc(-100% - 8px))",
      zIndex: 9999,
    };
  })();

  const getConfirmConfig = (type: ConfirmType) => {
    switch (type) {
      case "activate":
        return {
          title: "Activate employee?",
          lines: [
            "Are you sure you want to reactivate this employee?",
            "Reactivating this account will enable all associated permissions and allow the employee to access their dashboard.",
          ],
          note: "",
          confirmText: "Confirm",
          confirmVariant: "dark" as const,
          nextStatus: "active",
        };

      case "deactivate":
        return {
          title: "Deactivate employee?",
          lines: [
            "Are you sure you want to deactivate this employee?",
            "This employee will no longer be able to log in or access any system resources.",
          ],
          note: "",
          confirmText: "Deactivate",
          confirmVariant: "danger" as const,
          nextStatus: "deactivated",
        };

      case "archive":
        return {
          title: "Archive employee?",
          lines: [
            "Archiving hides this employee from your main view to keep your workspace organized.",
            "You can still access their history and reports later.",
          ],
          note: "The employee will lose all login access and system permissions immediately.",
          confirmText: "Confirm",
          confirmVariant: "dark" as const,
          nextStatus: "archived",
        };

      case "unarchive":
        return {
          title: "Unarchive employee?",
          lines: [
            "This will move the employee back to your main workspace and active list.",
          ],
          note: "Their login access and system permissions will be restored, allowing them to access the platform again.",
          confirmText: "Confirm",
          confirmVariant: "dark" as const,
          nextStatus: "active",
        };
    }
  };

  const handleConfirm = async () => {
    if (!confirmEmp || !confirmType) return;

    const cfg = getConfirmConfig(confirmType);

    setConfirmLoading(true);
    try {
      await updateStatus(confirmEmp._id, cfg.nextStatus);
      closeConfirm();
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="agency-employee-table-container">
      <table>
        <TableHeaderRow>
          <TableHeaderCell minWidth={260}>EMPLOYEE</TableHeaderCell>
          <TableHeaderCell minWidth={260}>AGENT</TableHeaderCell>
          <TableHeaderCell minWidth={140}>STATUS</TableHeaderCell>
          <TableHeaderCell minWidth={160}>CREATED ON</TableHeaderCell>
          <TableHeaderCell minWidth={160}>UPDATED ON</TableHeaderCell>
          <TableHeaderCell minWidth={90}>ACTION</TableHeaderCell>
        </TableHeaderRow>

        {listData?.map((data) => {
          const employeeName =
            data.full_name ||
            `${data.first_name || ""} ${data.last_name || ""}`.trim();

          const avatarUrl = getEmployeeProfileImageUrl(data.profile_photo);
          const effectiveStatus = statusOverride[data._id] ?? data.status;

          const normalizedStatus = (effectiveStatus || "").toLowerCase();

          const s = getStatusConfig(effectiveStatus);

          const toggleLabel =
            normalizedStatus === "active" ? "Deactivate" : "Activate";

          const archiveLabel =
            normalizedStatus === "archived" ? "Unarchive" : "Archive";

          return (
            <div key={data._id} className="row-wrapper">
              <TableDataRow
                className="contain-row clickable-row"
                onClick={() =>
                  navigate(`/agency-agent-employee/view/${data._id}?type=info`)
                }
              >
                <TableDataCell minWidth={260}>
                  <div className="employee-cell">
                    <div className="avatar">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt="employee"
                          onError={(e: any) => {
                            e.target.onerror = null;
                            e.target.src =
                              "/images/profile-placeholder-image.png";
                          }}
                        />
                      ) : (
                        <div className="avatar-fallback">
                          {getInitials(employeeName, data.email)}
                        </div>
                      )}
                    </div>

                    <div className="employee-details">
                      <div className="employee-name">{employeeName || "-"}</div>
                      <div className="employee-email">{data.email || "-"}</div>
                    </div>
                  </div>
                </TableDataCell>

                <TableDataCell minWidth={260}>
                  {(() => {
                    const agent =
                      typeof data.agent_id === "string" ? null : data.agent_id;

                    const agentName =
                      `${agent?.principal_agent_first_name || ""} ${agent?.principal_agent_last_name || ""}`.trim() ||
                      agent?.email ||
                      "-";

                    const agentEmail = agent?.email || "-";

                    const agentAvatarUrl = getEmployeeProfileImageUrl(
                      agent?.profile_image_ref,
                    );

                    return (
                      <div className="employee-cell">
                        <div className="avatar">
                          {agentAvatarUrl ? (
                            <img
                              src={agentAvatarUrl}
                              alt="agent employee"
                              onError={(e: any) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "/images/profile-placeholder-image.png";
                              }}
                            />
                          ) : (
                            <div className="avatar-fallback">
                              {getInitials(agentName, agentEmail)}
                            </div>
                          )}
                        </div>

                        <div className="employee-details">
                          <div className="employee-name">{agentName}</div>
                          <div className="employee-email">{agentEmail}</div>
                        </div>
                      </div>
                    );
                  })()}
                </TableDataCell>

                <TableDataCell minWidth={140}>
                  <div className={`status-badge ${s.className}`}>{s.label}</div>
                </TableDataCell>

                <TableDataCell minWidth={160}>
                  <div className="date-contain">
                    <p className="date-text">{formatDate(data.createdAt)}</p>
                    <p className="time-text">{formatTime(data.createdAt)}</p>
                  </div>
                </TableDataCell>

                <TableDataCell minWidth={160}>
                  <div className="date-contain">
                    <p className="date-text">{formatDate(data.updatedAt)}</p>
                    <p className="time-text">{formatTime(data.updatedAt)}</p>
                  </div>
                </TableDataCell>

                <TableDataCell minWidth={90} className="action-td">
                  <div
                    className="action-menu-wrapper"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="action-menu-btn"
                      onClick={(e: any) => {
                        const btnEl = e.currentTarget as HTMLElement;

                        if (openMenuId === data._id) {
                          setOpenMenuId(null);
                          setMenuMeta({ anchorRect: null, anchorEl: null });
                          return;
                        }

                        setOpenMenuId(data._id);
                        setMenuMeta({
                          anchorRect: btnEl.getBoundingClientRect(),
                          anchorEl: btnEl,
                        });
                      }}
                    >
                      &#8942;
                    </div>
                  </div>
                </TableDataCell>
              </TableDataRow>
            </div>
          );
        })}
      </table>

      {openMenuId && menuMeta.anchorRect && dropdownStyle
        ? createPortal(
            <div
              ref={dropdownRef}
              className="action-dropdown action-dropdown-portal"
              style={dropdownStyle}
            >
              {(() => {
                const emp = listData.find((x) => x._id === openMenuId);
                if (!emp) return null;

                const normalizedStatus = (emp.status || "").toLowerCase();
                const toggleLabel =
                  normalizedStatus === "active" ? "Deactivate" : "Activate";
                const archiveLabel =
                  normalizedStatus === "archived" ? "Unarchive" : "Archive";

                return (
                  <>
                    <div
                      className="action-item font-inter-400"
                      onClick={() => {
                        setOpenMenuId(null);
                        setMenuMeta({ anchorRect: null, anchorEl: null });

                        navigate(
                          `/agency-agent-employee/view/${emp._id}?type=info`,
                        );
                      }}
                    >
                      View
                    </div>

                    {/* <div
                      className="action-item font-inter-400"
                      onClick={() => openConfirmForActiveToggle(emp)}
                    >
                      {toggleLabel}
                    </div>

                    <div
                      className="action-item font-inter-400"
                      onClick={() => openConfirmForArchiveToggle(emp)}
                    >
                      {archiveLabel}
                    </div> */}
                  </>
                );
              })()}
            </div>,
            document.body,
          )
        : null}

      <Modal
        open={confirmOpen}
        onClose={closeConfirm}
        className="employee-confirm-modal"
        disableEscapeKeyDown={confirmLoading}
      >
        <div className="ecm-center" onClick={(e) => e.stopPropagation()}>
          <div className="ecm-card">
            {confirmType && (
              <>
                {(() => {
                  const cfg = getConfirmConfig(confirmType);

                  return (
                    <>
                      <p className="ecm-title">{cfg.title}</p>

                      <div className="ecm-body">
                        {cfg.lines.map((t, idx) => (
                          <p key={idx} className="ecm-text">
                            {t}
                          </p>
                        ))}

                        {cfg.note ? (
                          <div className="ecm-note">
                            <p className="ecm-note-title">Note:</p>
                            <p className="ecm-note-text">{cfg.note}</p>
                          </div>
                        ) : null}
                      </div>

                      <div className="ecm-actions">
                        <button
                          className="ecm-cancel"
                          onClick={closeConfirm}
                          disabled={confirmLoading}
                          type="button"
                        >
                          Cancel
                        </button>

                        <button
                          className={`ecm-confirm ${
                            cfg.confirmVariant === "danger" ? "danger" : ""
                          }`}
                          onClick={handleConfirm}
                          disabled={confirmLoading}
                          type="button"
                        >
                          {confirmLoading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            cfg.confirmText
                          )}
                        </button>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgencyAgentEmployeeTable;
